/**
 * Integrations Page
 *
 * Manage third-party integrations including Calendly
 */

import { CalendlyConnect, ScheduleList } from '@/components/integrations/calendly'
import { useIsCalendlyConnected } from '@/lib/integrations/calendly'

export function IntegrationsPage() {
  const { isConnected: isCalendlyConnected } = useIsCalendlyConnected()

  const otherIntegrations = [
    {
      id: 'google-calendar',
      name: 'Google Calendar',
      description: 'Sync your recording schedule with Google Calendar',
      category: 'calendar',
      comingSoon: true,
    },
    {
      id: 'zoom',
      name: 'Zoom',
      description: 'Automatically create Zoom meetings for recordings',
      category: 'recording',
      comingSoon: true,
    },
    {
      id: 'riverside',
      name: 'Riverside.fm',
      description: 'High-quality remote recording platform',
      category: 'recording',
      comingSoon: true,
    },
    {
      id: 'gmail',
      name: 'Gmail',
      description: 'Send guest invitations and follow-ups',
      category: 'communication',
      comingSoon: true,
    },
  ]

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">Integrations</h1>
        <p className="text-zinc-500 mt-1">Connect your favorite tools to streamline your workflow</p>
      </div>

      {/* Calendly Section */}
      <section className="mb-8">
        <h2 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-4">
          Scheduling
        </h2>

        <CalendlyConnect />

        {/* Show schedules if connected */}
        {isCalendlyConnected ? (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-3">
              Your Calendly Schedules
            </h3>
            <ScheduleList />
          </div>
        ) : (
          <div className="mt-4 p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/30 border border-zinc-200 dark:border-zinc-700">
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              Connect your Calendly account to let guests schedule recording times directly from your availability.
            </p>
          </div>
        )}
      </section>

      {/* Other Integrations */}
      <section>
        <h2 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-4">
          Other Integrations
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {otherIntegrations.map((integration) => (
            <div
              key={integration.id}
              className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6 opacity-60"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-zinc-900 dark:text-zinc-100">
                      {integration.name}
                    </h3>
                    {integration.comingSoon && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#FF6F00]/10 text-[#FF6F00] font-medium">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-zinc-500 mt-1">{integration.description}</p>
                  <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 capitalize">
                    {integration.category}
                  </span>
                </div>
                <button
                  disabled
                  className="px-3 py-1.5 rounded-lg border border-zinc-300 dark:border-zinc-700 text-sm font-medium text-zinc-400 cursor-not-allowed"
                >
                  Connect
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
